import { useContext, useEffect } from "react";
import {
  Grid,
  Container,
  Box,
  Typography,
  Button,
  Divider,
} from "@mui/material";
import { NavBar } from "../../components";
import { useNavigate } from "react-router-dom";
import AuthContext from "../../context/AuthContext";
import { useFetch } from "../../hooks/useFetch";

import AssignmentIcon from "@mui/icons-material/Assignment";
import ListAltIcon from "@mui/icons-material/ListAlt";
import HistoryIcon from "@mui/icons-material/History";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import HandymanIcon from "@mui/icons-material/Handyman";
import SpeedIcon from "@mui/icons-material/Speed";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";

const ORANGE = "#fbab35";
const DARK_TEXT = "#1e2a3a";
const MUTED = "#8c96a9";
const PAGE_BG = "#f0f2f8";

const baseCard = {
  background: "#fff",
  borderRadius: "16px",
  border: "1px solid rgba(140, 150, 169, 0.12)",
  boxShadow: "0 2px 12px rgba(30, 42, 58, 0.07)",
  padding: "24px",
  boxSizing: "border-box" as const,
};

const iconBox = {
  background: "#eef1f7",
  borderRadius: "12px",
  p: 1.2,
  display: "inline-flex",
  width: "fit-content",
};

const statLabel = {
  color: MUTED,
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.1em",
  textTransform: "uppercase" as const,
  mb: 0.5,
};

const actionButton = {
  background: ORANGE,
  color: "#fff",
  borderRadius: "10px",
  fontWeight: 600,
  textTransform: "none" as const,
  px: 2.5,
  py: 1,
  fontSize: "0.9rem",
  width: "fit-content",
  "&:hover": { background: "#e09a2a" },
};

interface RecentProposal {
  id: number;
  customName: string;
  date: string;
  well: { name: string };
}

interface RecentTally {
  id: number;
  customName: string;
  date: string;
  well: { name: string };
}

interface HomeStats {
  proposalsLastMonth: number;
  tallyLastMonth: number;
  recentProposals: RecentProposal[];
  recentTallies: RecentTally[];
}

const relativeTime = (dateStr: string): string => {
  const days = Math.floor(
    (Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24)
  );
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
};

export const HomePage = () => {
  const navigate = useNavigate();
  const { idRole } = useContext(AuthContext);

  const { data: statsResponse, isLoading } = useFetch<{
    success: boolean;
    data: HomeStats;
  }>("home-stats");

  const stats = statsResponse?.data;

  useEffect(() => {
    if (idRole === 3) navigate("/proposal");
  }, [idRole, navigate]);


  return (
    <>
      <NavBar title="Home" buttons={[]} />
      <Box
        sx={{
          marginTop: "80px",
          minHeight: "calc(100vh - 80px)",
          background: PAGE_BG,
          padding: { xs: 2, md: 4 },
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={3} alignItems="stretch">

            {/* ── Column 1: Proposals This Month · 3D Wellbore · Pressure Simulator ── */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3, height: "100%" }}>

                {/* Proposals This Month */}
                <Box sx={{ ...baseCard, padding: "16px", display: "flex", alignItems: "center", gap: 2 }}>
                  <Box sx={{ background: "#fdf3e3", borderRadius: "10px", p: 0.8, display: "inline-flex", flexShrink: 0 }}>
                    <AssignmentIcon sx={{ color: ORANGE, fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography sx={statLabel}>Proposals This Month</Typography>
                    <Typography sx={{ color: DARK_TEXT, fontSize: "1.6rem", fontWeight: 700, lineHeight: 1.1 }}>
                      {isLoading ? "—" : stats?.proposalsLastMonth ?? "—"}
                    </Typography>
                    <Typography sx={{ color: MUTED, fontSize: "0.75rem" }}>Last 30 days</Typography>
                  </Box>
                </Box>

                {/* 3D Wellbore */}
                <Box sx={{ ...baseCard, flexGrow: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box sx={iconBox}>
                    <ViewInArIcon sx={{ color: MUTED, fontSize: 28 }} />
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography sx={{ color: DARK_TEXT, fontSize: "1.25rem", fontWeight: 700, mb: 0.75 }}>
                      3D Wellbore
                    </Typography>
                    <Typography sx={{ color: MUTED, fontSize: "0.88rem", lineHeight: 1.6 }}>
                      Visualize and engineer complex downhole geometries with high-precision vector modeling.
                    </Typography>
                  </Box>
                  <Button sx={actionButton} onClick={() => window.open("https://3dwellbore.com/", "_blank")}>
                    Start Designer →
                  </Button>
                </Box>

                {/* Pressure Simulator */}
                <Box sx={{ ...baseCard, flexGrow: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box sx={iconBox}>
                    <SpeedIcon sx={{ color: MUTED, fontSize: 28 }} />
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography sx={{ color: DARK_TEXT, fontSize: "1.25rem", fontWeight: 700, mb: 0.75 }}>
                      Pressure Simulator
                    </Typography>
                    <Typography sx={{ color: MUTED, fontSize: "0.88rem", lineHeight: 1.6 }}>
                      Advanced hydraulic simulation with real-time gradient mapping and failure point analysis.
                    </Typography>
                  </Box>
                  <Button sx={actionButton} onClick={() => navigate("/sim/press")}>
                    Open Simulator →
                  </Button>
                </Box>

              </Box>
            </Grid>

            {/* ── Column 2: Tally Designs · Technical Design · Tally Design ── */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3, height: "100%" }}>

                {/* Tally Designs */}
                <Box sx={{ ...baseCard, padding: "16px", display: "flex", alignItems: "center", gap: 2 }}>
                  <Box sx={{ background: "#eef5ee", borderRadius: "10px", p: 0.8, display: "inline-flex", flexShrink: 0 }}>
                    <ListAltIcon sx={{ color: "#4caf50", fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography sx={statLabel}>Tally Designs</Typography>
                    <Typography sx={{ color: DARK_TEXT, fontSize: "1.6rem", fontWeight: 700, lineHeight: 1.1 }}>
                      {isLoading ? "—" : stats?.tallyLastMonth ?? "—"}
                    </Typography>
                    <Typography sx={{ color: MUTED, fontSize: "0.75rem" }}>Last 30 days</Typography>
                  </Box>
                </Box>

                {/* Technical Design */}
                <Box sx={{ ...baseCard, flexGrow: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box sx={iconBox}>
                    <HandymanIcon sx={{ color: MUTED, fontSize: 28 }} />
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography sx={{ color: DARK_TEXT, fontSize: "1.25rem", fontWeight: 700, mb: 0.75 }}>
                      Technical Design
                    </Typography>
                    <Typography sx={{ color: MUTED, fontSize: "0.88rem", lineHeight: 1.6 }}>
                      Comprehensive architectural tools for component-level engineering and specifications.
                    </Typography>
                  </Box>
                  <Button sx={actionButton} onClick={() => navigate("/tech/all")}>
                    Start Design →
                  </Button>
                </Box>

                {/* Tally Design */}
                <Box sx={{ ...baseCard, flexGrow: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box sx={iconBox}>
                    <ListAltIcon sx={{ color: MUTED, fontSize: 28 }} />
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography sx={{ color: DARK_TEXT, fontSize: "1.25rem", fontWeight: 700, mb: 0.75 }}>
                      Tally Design
                    </Typography>
                    <Typography sx={{ color: MUTED, fontSize: "0.88rem", lineHeight: 1.6 }}>
                      Detailed pipe tally management with automated length calculations and joint tracking.
                    </Typography>
                  </Box>
                  <Button sx={actionButton} onClick={() => navigate("/tally/all")}>
                    Manage Tally →
                  </Button>
                </Box>

              </Box>
            </Grid>

            {/* ── Column 3: Recent Proposals · Recent Exports ── */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3, height: "100%" }}>

                {/* Recent Proposals */}
                <Box sx={{ ...baseCard, flex: 1, overflow: "auto" }}>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ background: "#eef1f7", borderRadius: "10px", p: 1, display: "inline-flex" }}>
                      <HistoryIcon sx={{ color: MUTED, fontSize: 22 }} />
                    </Box>
                  </Box>
                  <Typography sx={{ ...statLabel, mb: 1.5 }}>Recent Proposals</Typography>
                  {isLoading ? (
                    <Typography sx={{ color: MUTED, fontSize: "0.88rem" }}>Loading…</Typography>
                  ) : !stats?.recentProposals?.length ? (
                    <Typography sx={{ color: MUTED, fontSize: "0.88rem" }}>No proposals yet</Typography>
                  ) : (
                    stats.recentProposals.map((p, i) => (
                      <Box key={p.id}>
                        <Box
                          onClick={() => navigate(`/tech/${p.id}`)}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 0.75,
                            cursor: "pointer",
                            borderRadius: "8px",
                            px: 0.5,
                            "&:hover": { background: "rgba(140,150,169,0.08)" },
                          }}
                        >
                          <Typography sx={{ color: DARK_TEXT, fontSize: "0.9rem", fontWeight: 500 }}>
                            {p.customName}
                          </Typography>
                          <Typography sx={{ color: MUTED, fontSize: "0.82rem", whiteSpace: "nowrap", ml: 1 }}>
                            {relativeTime(p.date)}
                          </Typography>
                        </Box>
                        {i < stats.recentProposals.length - 1 && (
                          <Divider sx={{ borderColor: "rgba(140,150,169,0.12)" }} />
                        )}
                      </Box>
                    ))
                  )}
                </Box>

                {/* Recent Tally Designs */}
                <Box sx={{ ...baseCard, flex: 1, overflow: "auto" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <FolderOpenIcon sx={{ color: MUTED, fontSize: 20 }} />
                    <Typography sx={{ color: DARK_TEXT, fontWeight: 700, fontSize: "1rem" }}>
                      Recent Tally Designs
                    </Typography>
                  </Box>
                  {isLoading ? (
                    <Typography sx={{ color: MUTED, fontSize: "0.88rem" }}>Loading…</Typography>
                  ) : !stats?.recentTallies?.length ? (
                    <Typography sx={{ color: MUTED, fontSize: "0.88rem" }}>No tally designs yet</Typography>
                  ) : (
                    stats.recentTallies.map((t, i) => (
                      <Box key={t.id}>
                        <Box
                          onClick={() => navigate(`/tally/${t.id}`)}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 1.25,
                            cursor: "pointer",
                            borderRadius: "8px",
                            px: 0.5,
                            "&:hover": { background: "rgba(140,150,169,0.08)" },
                          }}
                        >
                          <Box>
                            <Typography sx={{ color: DARK_TEXT, fontSize: "0.88rem", fontWeight: 500 }}>
                              {t.customName}
                            </Typography>
                            <Typography sx={{ color: MUTED, fontSize: "0.75rem" }}>
                              {t.well?.name} · {relativeTime(t.date)}
                            </Typography>
                          </Box>
                        </Box>
                        {i < stats.recentTallies.length - 1 && (
                          <Divider sx={{ borderColor: "rgba(140,150,169,0.12)" }} />
                        )}
                      </Box>
                    ))
                  )}
                </Box>

              </Box>
            </Grid>

          </Grid>
        </Container>
      </Box>
    </>
  );
};
